import React, { useState } from 'react';
import axios from 'axios';
import { UserX, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

/**
 * TimeOffPanel
 * ============
 * Per-engineer time off — different from Quick Block (studio-wide).
 * Use for: sick days, PTO, personal appointments.
 * Blocks only the selected engineer's calendar; the studio remains
 * available for other engineers.
 */
const engineers = [
  { id: 1, name: 'Trap Spade' },
  { id: 2, name: 'Chapo' },
  { id: 3, name: 'Dev' },
  { id: 4, name: 'Shotz' },
  { id: 5, name: 'Duse' },
  { id: 6, name: 'PB Slimeyy' },
  { id: 7, name: 'WickTheProducer' },
];

const times = [
  '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
  '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM',
  '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM',
];

const TimeOffPanel = () => {
  const [engineerId, setEngineerId] = useState(1);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('6:00 PM');
  const [hours, setHours] = useState(3);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        engineer_id: engineerId,
        start_date: startDate,
        end_date: endDate,
        all_day: allDay,
        reason: reason.trim() || 'Engineer time off',
      };
      if (!allDay) {
        body.start_time = startTime;
        body.hours = parseInt(hours, 10);
      }
      const r = await axios.post(`${API}/admin/time-off`, body);
      const engName = engineers.find(e => e.id === engineerId)?.name || 'engineer';
      toast.success(`✅ ${engName} blocked for ${r.data.slots_total} slots`);
      setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Time off failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="time-off-panel"
      className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40">
          <UserX className="w-5 h-5 text-cyan-300" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white">Engineer Time Off</h3>
          <p className="text-xs text-cyan-200/70">
            Block a single engineer's calendar (sick day, PTO, personal). Studio stays available for others.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-cyan-300 mb-1">Engineer</label>
          <select
            data-testid="to-engineer"
            value={engineerId}
            onChange={(e) => setEngineerId(parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
          >
            {engineers.map((eng) => (
              <option key={eng.id} value={eng.id}>{eng.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-cyan-300 mb-1">Reason (optional)</label>
          <input
            data-testid="to-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Sick, Out of town"
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-cyan-300 mb-1">Start Date</label>
          <input
            data-testid="to-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-cyan-300 mb-1">End Date</label>
          <input
            data-testid="to-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2 text-sm text-cyan-100 cursor-pointer select-none">
        <input
          data-testid="to-all-day"
          type="checkbox"
          checked={allDay}
          onChange={(e) => setAllDay(e.target.checked)}
          className="w-4 h-4 accent-cyan-500"
        />
        <span>All day (blocks entire studio operating hours 10AM–4AM)</span>
      </label>

      {!allDay && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-semibold text-cyan-300 mb-1">Start Time</label>
            <select
              data-testid="to-start-time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
            >
              {times.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-cyan-300 mb-1">Hours</label>
            <select
              data-testid="to-hours"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg bg-black/50 border border-cyan-500/40 text-white text-sm focus:outline-none focus:border-cyan-400"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                <option key={h} value={h}>{h} hr{h > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <button
        data-testid="to-submit"
        onClick={submit}
        disabled={submitting}
        className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-sm disabled:opacity-50 transition-all flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving…
          </>
        ) : (
          <>Mark Time Off</>
        )}
      </button>

      <p className="text-[10px] text-cyan-200/50 text-center mt-2">
        Only blocks the selected engineer&apos;s calendar. Other engineers stay bookable.
      </p>
    </div>
  );
};

export default TimeOffPanel;
